import { useEffect } from 'react'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { toast } from '@/clientWeb/components/ui/toast-utils'
import { useMeetingSessionStore } from '../store/meeting-session.store'

const TOAST_ID = 'system-mic-muted'

/**
 * The in-meeting toast for a system/hardware microphone mute, driven by the
 * `systemMicMuted` flag the media layer sets (see services/mic-interruption).
 *
 * Mounted once at the meeting level so it outlives the control bar auto-hiding.
 * A fixed id plus `Infinity` duration is one persistent card that updates in
 * place — never a growing stack — and stays until the mute clears; a long,
 * must-see notice is exactly what the notice stack's amber warning variant is
 * for. The person can still dismiss it by hand, and it returns the next time the
 * flag flips back on.
 *
 * The mic-button indicator in MicControl is the always-on signal; this is the
 * one-time explanation of what to do about it.
 */
export function useSystemMicMuteNotice(): void {
  const t = useTranslation()
  const systemMicMuted = useMeetingSessionStore((s) => s.systemMicMuted)

  useEffect(() => {
    if (!systemMicMuted) return
    toast.warning({
      id: TOAST_ID,
      duration: Infinity,
      title: t('controlBar.systemMicMutedTitle'),
      description: t('controlBar.systemMicMutedBody'),
    })
    return () => {
      toast.dismiss(TOAST_ID)
    }
  }, [systemMicMuted, t])
}
